import { supabase } from './supabase';
import type { InterviewRole, OutputLanguage } from '../types';

/**
 * Synthèse d'une réplique du recruteur par la voix ElevenLabs, via l'edge function `entretien-voix`.
 * La clé ElevenLabs ne quitte jamais le serveur ; l'app n'envoie que la réplique et le jeton que
 * `claude-interview` a signé avec elle.
 */

// « plafond » : limite de la période (semaine, ou mois en Free) ; « plafond_entretien » : garde-fou de
// caractères de cet entretien. Les deux finissent l'entretien à la voix du téléphone, dites autrement.
export type RaisonVoixIndisponible = 'plafond' | 'plafond_entretien' | 'indisponible';

/** Toujours rattrapée par l'appelant : la voix du téléphone prend le relais. */
export class VoixIndisponible extends Error {
  constructor(public readonly raison: RaisonVoixIndisponible) {
    super(`voix ElevenLabs indisponible : ${raison}`);
    this.name = 'VoixIndisponible';
  }
}

export interface DemandeVoix {
  texte: string;
  jeton: string;
  role: InterviewRole;
  langue: OutputLanguage;
  /** Tiré au début de l'entretien : la vraie voix se compte par entretien, pas par réplique. */
  entretienId: string;
}

/** Rend l'audio MP3 en base64. */
export async function synthetiserReplique({ texte, jeton, role, langue, entretienId }: DemandeVoix): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke<{ audio?: string }>('entretien-voix', {
      body: { texte, jeton, role, langue, entretienId },
    });
    if (error) {
      const contexte = (error as { context?: { status?: number; json?: () => Promise<unknown> } }).context;
      if (contexte?.status !== 429) throw new VoixIndisponible('indisponible');
      let code: unknown;
      try {
        code = ((await contexte.json?.()) as { error?: unknown } | undefined)?.error;
      } catch {
        code = undefined;
      }
      throw new VoixIndisponible(code === 'plafond_entretien' ? 'plafond_entretien' : 'plafond');
    }
    if (typeof data?.audio !== 'string' || !data.audio) throw new VoixIndisponible('indisponible');
    return data.audio;
  } catch (e) {
    if (e instanceof VoixIndisponible) throw e;
    throw new VoixIndisponible('indisponible');
  }
}

export interface VoixRestante {
  restants: number;
  limite: number;
  periode: 'semaine' | 'mois';
  /** Premier jour de la période suivante, AAAA-MM-JJ. */
  renouvellement: string;
}

/**
 * Entretiens avec la vraie voix qu'il reste dans la période. `null` sur toute erreur : l'écran n'en
 * dit alors rien, plutôt qu'un nombre deviné.
 */
export async function lireVoixRestante(): Promise<VoixRestante | null> {
  try {
    const { data, error } = await supabase.functions.invoke<VoixRestante>('entretien-voix', { method: 'GET' });
    if (error || !data) return null;
    const lisible =
      typeof data.restants === 'number' &&
      typeof data.limite === 'number' &&
      (data.periode === 'semaine' || data.periode === 'mois') &&
      typeof data.renouvellement === 'string';
    return lisible ? data : null;
  } catch {
    return null;
  }
}
